"use client";

import { useMemo, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useCurrentAccountId } from "@zapier/identity";
import { getMetaCookie, setMetaCookie } from "@zapier/utils";
import { useSupportedAssets } from "@zapier/client";

const ASSET_SIDEBAR_COOKIE = "showAssetsSidebar";

const useAssetSidebar = () => {
  const { isMPFEnabled, isLoading } = useSupportedAssets();
  const pathname = usePathname();
  const accountId = useCurrentAccountId();
  const [staleCacheValue, setStaleCacheValue] = useState<boolean | undefined>();

  // Load stale cache value on mount
  useEffect(() => {
    if (!pathname || !accountId) return;

    try {
      const cachedMeta = getMetaCookie(accountId);
      if (cachedMeta && typeof cachedMeta[ASSET_SIDEBAR_COOKIE] === "boolean") {
        // Use cached value if it matches current pathname context
        const cachedShouldShow =
          cachedMeta[ASSET_SIDEBAR_COOKIE] &&
          !pathname.includes("/connections") &&
          !pathname.includes("/templates");
        setStaleCacheValue(cachedShouldShow);
      }
    } catch {
      // If parsing fails, just continue without cache
    }
  }, [pathname, accountId]);

  const showAssetsSidebar = useMemo(() => {
    // Exclude connections and templates routes from this layout, even though they are an /assets/ subpath, they are different from a product standpoint
    // This is only used in the assets layout, so projects and any other non-assets path is already excluded
    return (
      isMPFEnabled &&
      !pathname?.includes("/connections") &&
      !pathname?.includes("/templates") &&
      !pathname?.includes("/app/assets/zaps/approvals")
    );
  }, [isMPFEnabled, pathname]);

  // Update cookie if the computed value changes after split treatments load
  useEffect(() => {
    if (!isLoading && pathname && accountId) {
      setMetaCookie(accountId, {
        [ASSET_SIDEBAR_COOKIE]: showAssetsSidebar,
      });
    }
  }, [showAssetsSidebar, pathname, isLoading, accountId]);

  // Use stale cache value while loading, otherwise use computed value
  const effectiveShowAssetsSidebar =
    isLoading && staleCacheValue ? staleCacheValue : showAssetsSidebar;

  return {
    showAssetsSidebar: effectiveShowAssetsSidebar,
    isLoading: isLoading && !staleCacheValue,
  };
};

export default useAssetSidebar;
